/**
 * V2 长图双态 —— **结果语言映射表(单源)**。
 *
 * ═══ 为什么必须是一张表,而不是各面板自己写文案 ═══════════════════════════════════
 * 小白态与精编态说的是**同一批旋钮**:主次、渲染档、排法、版式、图从哪来。
 * 两边各写一套文案的结局是已经踩过的那个:同一个概念在两处漂成两个意思,
 * 而 tsc / lint / 单测一个都拦不住(V0 第一轮 codex P2 = 「这份配色是哪个预设」各写一遍)。
 * 所以把**精编标签 + 小白标签 + 为什么**三列并排放在同一行:
 * 改一个档位的说法,两态必然一起改。
 *
 * ═══ 结果语言的判据 ═══════════════════════════════════════════════════════════
 * 小白态不出现 A/B/C、hero/minor、styleTag、styleTokens、原语、33/6 这些字样
 * (设计方案 §3.6 纪律)。这不是"换个好听的名字",而是换一个**说结果不说手段**的问法:
 *   renderMode  → 「这块的数字要一字不差吗?」—— 用户关心的是数字会不会被改,不是档位代号
 *   emphasis    → 「这块的分量」—— 换的是真画幅,所以说"放大/缩小"而不是 hero/minor
 *   genMode     → 「这块的画面从哪来?」—— i2i/t2i 是通路名,用户要判断的是像不像实物
 *
 * ★渲染档给**三个**选项而不是设计稿上的两个:阶段一规划出来的块可能就是 C
 * (数据块 planner 常给 C),只给「要 / 随意」两个按钮的话,C 档的块要么显示成没选中、
 * 要么被当成 B —— 「概览比实际乐观」在这个仓里已经踩过三次。三个选项都用结果语言说得清,
 * 就没有理由把真状态藏起来。
 */
import { STYLE_META } from "./blocks";
import type { BlockEmphasis, BlockLayout, RenderMode } from "./canvas";

/** 小白态一行「问-选-为什么」的一个选项。 */
export interface EasyChoice<V extends string> {
  value: V;
  /** 精编态标签(管线语言,现状原样;**不进** easyTextsOf 的反向锁面)。 */
  proLabel: string;
  /** 小白态标签(结果语言)。 */
  easyLabel: string;
  /** 小白态「为什么」—— 让用户能自己判断该选哪个,不是把选择藏起来。 */
  why: string;
}

export const RENDER_MODE_QUESTION = "这块的数字要一字不差吗?";
export const RENDER_MODE_CHOICES: ReadonlyArray<EasyChoice<RenderMode>> = [
  {
    value: "A",
    proLabel: "A · 全AI渲染",
    easyLabel: "随意 · 更好看",
    why: "整块交给出图模型,文字长在画面里最好看;数值有极小改写风险,不对就重出这一块。",
  },
  {
    value: "B",
    proLabel: "B · AI皮+精确数字",
    easyLabel: "要 · 好看且不差",
    why: "底板由出图模型画,数字由程序叠上去,必然零错 —— 参数 / 价格 / 榜单推荐这一档。",
  },
  {
    value: "C",
    proLabel: "C · 精确矢量",
    easyLabel: "要 · 朴素最稳",
    why: "整块由程序排版,零风险但观感朴素;二维码固定走这一档。",
  },
];

export const EMPHASIS_QUESTION = "这块的分量";
export const EMPHASIS_CHOICES: ReadonlyArray<EasyChoice<BlockEmphasis>> = [
  {
    value: "hero",
    proLabel: "重点",
    easyLabel: "放大重点",
    why: "画幅提一档,给最想让人记住的那一块(建议全篇只放大 1~2 块)。",
  },
  { value: "normal", proLabel: "常规", easyLabel: "正常", why: "按内容多少自动定画幅。" },
  {
    value: "minor",
    proLabel: "次要",
    easyLabel: "缩小次要",
    why: "画幅降一档,过渡块 / 补充说明别占版面。",
  },
];

export const GEN_MODE_QUESTION = "这块的画面从哪来?";
/** 「实拍」的主语按用途换(2026-08-03 开卡批:corp 语境下「产品实拍图」是错词)。 */
const GEN_MODE_SUBJECT: Record<string, string> = {
  corp: "公司",
  hire: "团队",
  event: "活动",
  menu: "菜品",
};
export function genModeChoices(useCase?: string | null): ReadonlyArray<EasyChoice<"i2i" | "t2i">> {
  const subject = GEN_MODE_SUBJECT[useCase ?? ""] ?? "产品";
  return [
    {
      value: "i2i",
      proLabel: `${subject}图生图`,
      easyLabel: `用${subject}实拍图`,
      why:
        subject === "产品"
          ? "成品里的产品和你上传的照片一模一样,不会被改样子。"
          : `成品里的画面以你上传的${subject}照片为底,不会被改样子。`,
    },
    {
      value: "t2i",
      proLabel: "文生图",
      easyLabel: "让 AI 画",
      why: "按你写的画面描述从零画一张,自由度最高,但细节可能和实物不同。",
    },
  ];
}
/** 兼容旧引用 = goods 词面。 */
export const GEN_MODE_CHOICES: ReadonlyArray<EasyChoice<"i2i" | "t2i">> = genModeChoices("goods");

export const LAYOUT_QUESTION = "画面和文字怎么摆";
export const LAYOUT_CHOICES: ReadonlyArray<EasyChoice<BlockLayout>> = [
  { value: "full", proLabel: "通栏", easyLabel: "整块通栏", why: "内容占满整行,最醒目。" },
  {
    value: "img_top_text_bottom",
    proLabel: "上图下文",
    easyLabel: "上图下字",
    why: "画面在上、文字在下,最常见的读法。",
  },
  {
    value: "img_left_text_right",
    proLabel: "左图右文",
    easyLabel: "左图右字",
    why: "画面在左、文字在右,适合并排讲一件事。",
  },
  {
    value: "two_col",
    proLabel: "双栏",
    easyLabel: "左右两栏",
    why: "两列并排,条目多时更省版面。",
  },
];

/** 「换个排法」缩略图的形状 —— 只画版式示意,不写样式名(小白态不认样式库)。 */
export type EasyShape =
  | "full"
  | "split-tb"
  | "split-lr"
  | "grid"
  | "lines"
  | "bars"
  | "big"
  | "quote"
  | "table"
  | "chart"
  | "circle";

export const STYLE_TAG_QUESTION = "换个排法";

/**
 * 33 个 styleTag → 小白说法 + 缩略图形状。
 * ★与 `STYLE_META` 逐键对齐(单测遍历真常量,漏一个必红);两边都要改的时候一起改。
 * ★小白名不是 `STYLE_META.label` 的复制:「Hero」「CTA」「主视觉KV」是行内黑话,
 *   照抄过来等于把术语从代码里搬到用户脸上。
 */
export const STYLE_TAG_LANGUAGE: Record<string, { easyLabel: string; shape: EasyShape }> = {
  // 文字
  bigtext: { easyLabel: "大标题", shape: "big" },
  golden: { easyLabel: "一句金句", shape: "quote" },
  body: { easyLabel: "整段文字", shape: "lines" },
  cite: { easyLabel: "引用一句", shape: "quote" },
  cta: { easyLabel: "行动召唤", shape: "big" },
  numlist: { easyLabel: "编号清单", shape: "lines" },
  twocol: { easyLabel: "左右两栏", shape: "split-lr" },
  // 图片
  hero: { easyLabel: "首屏大图", shape: "full" },
  scene: { easyLabel: "卖点画面", shape: "full" },
  white: { easyLabel: "白底产品图", shape: "full" },
  usage: { easyLabel: "使用画面", shape: "full" },
  real: { easyLabel: "实拍照片", shape: "full" },
  imagecard: { easyLabel: "图配文卡片", shape: "split-tb" },
  // ★实拍分格卡:多张实拍并排 + 每张矢量说明(2026-08-05 对齐模板样图)
  photocards: { easyLabel: "实拍分格卡", shape: "grid" },
  grid9: { easyLabel: "多图拼贴", shape: "grid" },
  kv: { easyLabel: "主视觉大图", shape: "full" },
  // 数据
  spec: { easyLabel: "参数清单", shape: "lines" },
  comparebar: { easyLabel: "横条对比", shape: "bars" },
  percent: { easyLabel: "百分比数字", shape: "big" },
  beforeafter: { easyLabel: "前后对照", shape: "split-lr" },
  stat: { easyLabel: "大数字", shape: "big" },
  timeline: { easyLabel: "时间顺序", shape: "lines" },
  flow: { easyLabel: "步骤流程", shape: "grid" },
  infocard: { easyLabel: "信息清单", shape: "lines" },
  rank: { easyLabel: "排行清单", shape: "lines" },
  table: { easyLabel: "表格", shape: "table" },
  price: { easyLabel: "价目清单", shape: "lines" },
  line: { easyLabel: "趋势曲线", shape: "chart" },
  pie: { easyLabel: "占比圆图", shape: "circle" },
  rating: { easyLabel: "星级评分", shape: "lines" },
  sales: { easyLabel: "销量好评", shape: "lines" },
  honor: { easyLabel: "资质荣誉", shape: "grid" },
  media: { easyLabel: "媒体报道", shape: "grid" },
  qr: { easyLabel: "扫码区", shape: "circle" },
};

/** 小白名;未登记的 tag 一律回落成中性说法 —— **绝不把原始 key 吐给用户**。 */
export function easyStyleTagLabel(styleTag: string): string {
  return STYLE_TAG_LANGUAGE[styleTag]?.easyLabel ?? "这一块";
}

export function easyShapeOf(styleTag: string): EasyShape {
  return STYLE_TAG_LANGUAGE[styleTag]?.shape ?? "lines";
}

/**
 * 小白态**禁用词**。反向锁单测按这张表逐字筛 `easyTextsOf()`;
 * 页面级反向锁(渲染后的 DOM)在 `EasyModePanel.test.tsx`。
 *
 * ★「视觉 DNA / 全局风格 / 样式标签」**不在这张表里** —— 它们由 V0 的
 * `namingDiscipline.test.ts` 在**更大的面**上锁着(扫 features/poster 全部非测试源文,
 * 不只小白态)。写进这里反而会让那个更严的守门测试抓到这张表本身
 * (禁用词清单里的字面量对扫描器来说也是一处出现)—— 两道锁重叠时,
 * 留严的那道,别为了凑齐清单去绕过守门测试。
 */
export const EASY_FORBIDDEN_TERMS: readonly string[] = [
  "hero",
  "minor",
  "normal",
  "emphasis",
  "renderMode",
  "styleTag",
  "styleTokens",
  "primitive",
  "palette",
  "i2i",
  "t2i",
  "原语",
  "33",
];

/** 全部**对客**字串 —— 反向锁的输入面(精编标签故意不收进来)。 */
export function easyTextsOf(): string[] {
  const out: string[] = [
    RENDER_MODE_QUESTION,
    EMPHASIS_QUESTION,
    GEN_MODE_QUESTION,
    LAYOUT_QUESTION,
    STYLE_TAG_QUESTION,
  ];
  for (const c of [
    ...RENDER_MODE_CHOICES,
    ...EMPHASIS_CHOICES,
    ...GEN_MODE_CHOICES,
    ...LAYOUT_CHOICES,
  ]) {
    out.push(c.easyLabel, c.why);
  }
  for (const tag of Object.keys(STYLE_META)) out.push(easyStyleTagLabel(tag));
  return out;
}
